<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        />

        <v-form ref="form" v-model="valid" lazy-validation v-if="!loading">
          <v-card color="primary" dark max-width="650px" v-if="!loading" tile>
            <v-card-title>
              <span class="headline" v-if="isEditView"
                >{{ editedItem.name }}
              </span>
              <span class="headline" v-else>{{
                $t("users.serviceProvidersAddEdit.newServiceProfessional")
              }}</span>
            </v-card-title>
          </v-card>

          <v-tabs fixed-tabs background-color="primary" dark v-model="tab">
            <v-tab>
              <v-icon class="mr-2">mdi-information</v-icon>
              {{ $t("users.serviceProvidersAddEdit.editInformation") }}
            </v-tab>
            <v-tab>
              <v-icon class="mr-2">mdi-cog</v-icon>
              {{ $t("users.serviceProvidersAddEdit.editUsers") }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-card max-width="650px" v-if="!loading">
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.name"
                          :label="$t('users.serviceProvidersAddEdit.name')"
                          color="primary"
                          :rules="serviceProviderNameRule"
                          required
                          persistent-hint
                          hint="Required"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-autocomplete
                          autocomplete="off"
                          v-model="editedItem.vendor_uuids"
                          :label="
                            $t('users.serviceProvidersAddEdit.manufacturerName')
                          "
                          :items="vendorOptions"
                          item-value="uuid"
                          item-text="name"
                          :search-input.sync="vendorSearch"
                          color="primary"
                          @input.native="loadVendors($event, $data)"
                          chips
                          deletable-chips
                          multiple
                          required
                          persistent-hint=""
                          :hint="$t('users.serviceProvidersAddEdit.required')"
                          hide-selected
                          :rules="[required]"
                        >
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-combobox
                          v-model="editedItem.domains"
                          :items="domainOptions"
                          :search-input.sync="domainSearch"
                          color="primary"
                          hide-selected
                          :label="
                            $t('users.serviceProvidersAddEdit.addCategory')
                          "
                          multiple
                          persistent-hint
                          chips
                          deletable-chips
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title
                                  v-html="
                                    $t(
                                      'users.serviceProvidersAddEdit.noResultsMatchingPressEnterToCreateANewOne',
                                      { domainSearch: domainSearch }
                                    )
                                  "
                                >
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-combobox>
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.address"
                          :label="$t('users.serviceProvidersAddEdit.address')"
                          color="primary"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.city"
                          :label="$t('users.serviceProvidersAddEdit.city')"
                          color="primary"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <country-selector
                          :initial-country="editedItem.country"
                          @change="editedItem.country = $event"
                        >
                        </country-selector>
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.email"
                          :label="$t('users.serviceProvidersAddEdit.email')"
                          color="primary"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.website"
                          :label="$t('users.serviceProvidersAddEdit.website')"
                          color="primary"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.phone"
                          :label="$t('users.serviceProvidersAddEdit.phone')"
                          color="primary"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" text @click="close">{{
                    $t("users.serviceProvidersAddEdit.cancel")
                  }}</v-btn>
                  <v-btn color="primary" text @click="save">{{
                    $t("users.serviceProvidersAddEdit.save")
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" v-if="editedItem.users.length !== 0">
                      <v-card-subtitle class="mb-0 pb-0">{{
                        $t("users.serviceProvidersAddEdit.addedUsers")
                      }}</v-card-subtitle>
                      <v-list
                        v-for="(user, index) in editedItem.users"
                        :key="user.uuid"
                      >
                        <user-email-component
                          :user="user"
                          :readonly="false"
                          @delete-user="deleteUserDialog(index)"
                        ></user-email-component>
                      </v-list>
                    </v-col>
                    <v-col cols="12">
                      <v-card-subtitle class="mb-0 pb-0">{{
                        $t("users.serviceProvidersAddEdit.inviteUsers")
                      }}</v-card-subtitle>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
      </v-layout>
      <warning-dialog
        :dialog="cancelDialog.active"
        :text="cancelDialog.text"
        :title="cancelDialog.title"
        @agree="handleCancelDialogOutput"
        :confirm="$t('users.serviceProvidersAddEdit.yes')"
        :reject="$t('users.serviceProvidersAddEdit.no')"
        v-show="false"
      />
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import snackbar from "@/utils/snackbar.js";
import UserEmailComponent from "@/components/miscellaneous/UserEmailComponent";
import CountrySelector from "@/components/selectors/CountrySelector.vue";

export default {
  components: {
    UserEmailComponent,
    WarningDialog,
    CountrySelector,
  },
  data: () => ({
    tab: null,
    valid: true,
    serviceProviderNameRule: [],
    loading: false,
    isEditView: false,
    groupOptions: [],
    vendorSearch: "",
    vendorOptions: [],
    domainSearch: "",
    domainOptions: [],
    editedItem: {
      users: [],
      name: "",
      crm_group_uuid: "",
      vendor_uuids: [],
      brands: [],
      dealer: [],
      vendor: null,
      domains: [],
      address: "",
      city: "",
      country: "",
      email: "",
      website: "",
      phone: "",
    },
    indexToDelete: null,
    cancelDialog: {
      active: false,
      title: "",
      text: "",
    },
  }),

  computed: {},

  watch: {
    tab(newValue, oldValue) {
      if (oldValue !== null) {
        let tab = newValue === 0 ? "info" : "users";
        this.$router.replace({
          name: this.$route.name,
          params: {
            id: this.$route.params.id,
            tab: tab,
          },
        });
      }
    },
  },

  created() {
    this.setInitalData();
    if (this.$route.name === "serviceProviderEdit") {
      this.loading = true;
      this.isEditView = true;
      api.getServiceProvider(this.$route.params.id).then((resp) => {
        this.editedItem = resp.data;
        this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
        this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
        this.editedItem.vendor_uuids = [];
        this.editedItem.vendors.forEach((vendor) =>
          this.editedItem.vendor_uuids.push(vendor.uuid)
        );
        this.loading = false;
      });
    }
    api.vendorAutocomplete("").then((resp) => {
      resp.data.results.forEach((element) => {
        this.vendorOptions.push(element);
      });
    });
    api.groupsAutocomplete("").then((resp) => {
      resp.data.forEach((element) => {
        this.groupOptions.push(element);
      });
    });
    api.itemCategoriesAutocomplete("").then((resp) => {
      resp.data.forEach((element) => {
        if (!this.domainOptions.includes(element)) {
          this.domainOptions.push(element);
        }
      });
    });
  },

  methods: {
    setInitalData() {
      this.serviceProviderNameRule = [
        (v) =>
          !!v ||
          this.$t(
            "users.serviceProvidersAddEdit.serviceProfessionalNameIsRequired"
          ),
      ];
      this.cancelDialog = {
        active: false,
        title: this.$t("users.serviceProvidersAddEdit.removeUser"),
        text: this.$t(
          "users.serviceProvidersAddEdit.areYouSureYouWantToRemoveThisUser"
        ),
      };
    },
    required(value) {
      if (value instanceof Array && value.length == 0) {
        return this.$t(
          "users.serviceProvidersAddEdit.manufacturerNameIsRequired"
        );
      }
      return (
        !!value ||
        this.$t("users.serviceProvidersAddEdit.manufacturerNameIsRequired")
      );
    },
    // inviteUser(user, done) {
    //   api
    //     .inviteDealerUser(this.$route.params.id, {
    //       email: user,
    //     })
    //     .then((resp) => {
    //       snackbar.success("User successfully invited");
    //       api.getDealer(this.$route.params.id).then((response) => {
    //         let findUser = response.data.users.find(
    //           (item) => item.username === resp.data.email
    //         );
    //         if (findUser) {
    //           this.editedItem.users.push(findUser);
    //           done(resp, true);
    //         } else {
    //           done(resp, false);
    //         }
    //       });
    //     });
    // },
    // rejectUser(user, done) {
    //   api.rejectInviteDealerUser(user.token).then((reps) => {
    //     snackbar.success("User successfully rejected");
    //     done(user);
    //   });
    // },
    deleteUserDialog(index) {
      this.indexToDelete = index;
      this.cancelDialog.active = true;
    },
    handleCancelDialogOutput(agreed) {
      this.loading = false;
      if (agreed) {
        api
          .deleteUserFromDealers(this.$route.params.id, {
            uuid: this.editedItem.users[this.indexToDelete].uuid,
          })
          .then((resp) => {
            this.editedItem.users.splice(this.indexToDelete, 1);
            snackbar.success(
              this.$t("utils.snackbar.success.userSuccessfullyRemoved")
            );
            this.indexToDelete = null;
          });
      } else {
        this.indexToDelete = null;
      }
      this.cancelDialog.active = false;
    },

    close() {
      this.$router.push({
        name: "serviceProviders",
      });
    },

    save() {
      if (this.$refs.form.validate()) {
        if (this.isEditView) {
          api
            .updateServiceProvider(this.editedItem.uuid, this.editedItem)
            .then((resp) => {
              snackbar.success(
                this.$t("utils.snackbar.success.successfullySaved")
              );
              this.$router.push({
                name: "serviceProviders",
              });
            })
            .catch((err) => {
              snackbar.error(
                this.$t(
                  "utils.snackbar.error.dontHavePermissionToEditServicePartner"
                )
              );
            });
        } else {
          api
            .createServiceProvider(this.editedItem)
            .then((resp) => {
              snackbar.success(
                this.$t("utils.snackbar.success.successfullySaved")
              );
              this.$router.push({
                name: "serviceProviders",
              });
            })
            .catch((err) => {
              snackbar.error(
                this.$t(
                  "utils.snackbar.error.dontHavePermissionToAddNewServicePartner"
                )
              );
            });
        }
      } else {
        snackbar.error();
      }
    },
  },
};
</script>
